/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useId, useState } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { CharacterCounter, ErrorMessage, HelperMessage, MessageWrapper } from '@atlaskit/form';
import { Inline } from '@atlaskit/primitives/compiled';
import Textarea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

const textareaLabelMessage = defineMessage({
	id: 'rovo-memories.memory-edit-input.MemoryEditInput.label',
	defaultMessage: 'Enter your memory observation edits',
	description: 'Label text needed for screen readers to describe the textarea input',
});

const placeholderMessage = defineMessage({
	id: 'rovo-memories.memory-edit-input.MemoryEditInput.placeholder',
	defaultMessage: 'My main priority this half is...',
	description: 'Placeholder text shown inside the memory edit text input',
});

const cancelButtonMessage = defineMessage({
	id: 'rovo-memories.memory-edit-input.MemoryEditInput.cancelButton',
	defaultMessage: 'Cancel',
	description: 'Label for the cancel button in the memory edit input',
});

const addButtonMessage = defineMessage({
	id: 'rovo-memories.memory-edit-input.MemoryEditInput.addButton',
	defaultMessage: 'Save',
	description: 'Label for the add/submit button in the memory edit input',
});

const helperTextMessage = defineMessage({
	id: 'rovo-memories.memory-edit-input.MemoryEditInput.helperText',
	defaultMessage: 'Update or add context to your current role, team or projects',
	description: 'Helper text shown below the memory edit textarea to guide the user',
});

const errorTextMessage = defineMessage({
	id: 'rovo-memories.memory-edit-input.MemoryEditInput.errorText',
	defaultMessage: 'Something went wrong saving your changes. Please try again.',
	description: 'Error message shown below the memory edit textarea when saving fails',
});

const MEMORY_EDIT_CHARACTER_LIMIT = 350;

const styles = cssMap({
	root: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: token('space.100'),
		width: '100%',
	},
	textfieldWrapper: {
		flex: '1 1 1rem',
		height: 'max-content',
	},
	messageRow: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) max-content',
		columnGap: token('space.100'),
		alignItems: 'start',
		width: '100%',
	},
	message: {
		minWidth: 0,
	},
	characterCounter: {
		justifySelf: 'end',
	},
	buttons: {
		flexShrink: 0,
	},
});

export interface MemoryEditInputProps {
	/**
	 * Initial value for the input. Defaults to empty string.
	 */
	defaultValue?: string;
	/**
	 * Called when the user submits the input via the "Add" button.
	 * Receives the current text value.
	 */
	onAdd: (value: string) => void;
	/**
	 * Called when the user clicks the "Cancel" button.
	 */
	onCancel: () => void;
	/**
	 * When true, a save is in flight: the Save button shows a spinner and the
	 * textarea and buttons are disabled. The input remains visible.
	 */
	isSubmitting?: boolean;
	/**
	 * When true, the previous save failed: an inline error message is shown and
	 * the input stays open so the user can retry.
	 */
	hasError?: boolean;
}

export const MemoryEditInput = ({
	defaultValue = '',
	onAdd,
	onCancel,
	isSubmitting = false,
	hasError = false,
}: MemoryEditInputProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const textareaId = useId();
	const [value, setValue] = useState(defaultValue);
	const isOverCharacterLimit = value.length > MEMORY_EDIT_CHARACTER_LIMIT;
	const characterCounterMessage = `${value.length}/${MEMORY_EDIT_CHARACTER_LIMIT}`;

	return (
		<div css={styles.root}>
			<div css={styles.textfieldWrapper}>
				<Textarea
					id={textareaId}
					autoFocus
					appearance="standard"
					resize="smart"
					minimumRows={1}
					maxHeight="4.5rem"
					value={value}
					placeholder={formatMessage(placeholderMessage)}
					onChange={(e) => setValue(e.target.value)}
					aria-label={formatMessage(textareaLabelMessage)}
					aria-describedby={`${textareaId}-character-counter`}
					isDisabled={isSubmitting}
					isInvalid={hasError || isOverCharacterLimit}
				/>
				<div css={styles.messageRow}>
					<div css={styles.message}>
						<MessageWrapper>
							{hasError ? (
								<ErrorMessage>{formatMessage(errorTextMessage)}</ErrorMessage>
							) : (
								<HelperMessage>{formatMessage(helperTextMessage)}</HelperMessage>
							)}
						</MessageWrapper>
					</div>
					<div css={styles.characterCounter}>
						<CharacterCounter
							currentValue={value}
							maxCharacters={MEMORY_EDIT_CHARACTER_LIMIT}
							inputId={textareaId}
							shouldShowAsError={isOverCharacterLimit}
							underMaximumMessage={characterCounterMessage}
							overMaximumMessage={characterCounterMessage}
						/>
					</div>
				</div>
			</div>
			<Inline xcss={styles.buttons} space="space.100" alignBlock="start">
				<Button
					testId="memory-edit-input-cancel"
					appearance="subtle"
					isDisabled={isSubmitting}
					onClick={() => {
						sendAnalyticsEvent({
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'cancelAddMemoryEdit',
							source: 'memoryModalSubscreen',
						});
						onCancel();
					}}
				>
					{formatMessage(cancelButtonMessage)}
				</Button>
				<Button
					testId="memory-edit-input-save"
					appearance="primary"
					isLoading={isSubmitting}
					onClick={() => {
						const trimmed = value.trim();
						if (trimmed && !isOverCharacterLimit) {
							sendAnalyticsEvent({
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'confirmAddMemoryEdit',
								source: 'memoryModalSubscreen',
							});
							onAdd(trimmed);
						}
					}}
					isDisabled={isSubmitting || !value.trim() || isOverCharacterLimit}
				>
					{formatMessage(addButtonMessage)}
				</Button>
			</Inline>
		</div>
	);
};
